/* Presence heartbeat — writes the signed-in user's `lastSeen` timestamp to
   Firestore at a low cadence while the tab is visible. The admin user
   dashboard reads this to compute "currently online" and "active this week".

   Cost model: 1 write per user every 5 minutes while visible. 100 users
   averaging 30 min/day of active time = ~600 writes/day total. Well under
   Firestore's free-tier quota.

   Public API:
     start(uid)   — begin heartbeat for the given uid (idempotent)
     stop()       — stop heartbeat (call on signout)

   The heartbeat updates two paths:
     /users/{uid}                  — top-level lastSeen, captured by the
                                     admin dashboard's onSnapshot listener
     /users/{uid}/meta/profile     — same value mirrored here for the
                                     existing profile-reading code paths
*/

import { OFFLINE_MODE, firebaseConfig } from "./firebase-config.js";
import { currentUser } from "./auth.js";

const HEARTBEAT_MS = 5 * 60 * 1000;   // 5 minutes — within free-tier quota
const FIRST_PING_MS = 1500;           // first heartbeat ~immediately on signin

let _state = null; // { uid, intervalId, paused }
let _fsP = null;   // promise → { db, doc, setDoc }

async function fs() {
  if (_fsP) return _fsP;
  _fsP = (async () => {
    const [{ getFirestore, doc, setDoc }, { getApp, getApps, initializeApp }] = await Promise.all([
      import("https://www.gstatic.com/firebasejs/10.12.0/firebase-firestore.js"),
      import("https://www.gstatic.com/firebasejs/10.12.0/firebase-app.js"),
    ]);
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    return { db: getFirestore(app), doc, setDoc };
  })();
  _fsP.catch(() => { _fsP = null; });
  return _fsP;
}

async function ping(uid) {
  if (OFFLINE_MODE || !uid) return;
  try {
    const { db, doc, setDoc } = await fs();
    const u = currentUser() || {};
    const now = Date.now();
    // Mirror Firebase Auth's email/displayName into Firestore on every ping.
    // Without this, the admin dashboard can't show user names — Auth-only
    // claims aren't readable cross-user from the client. Same write, just
    // a few extra fields, so no extra quota cost.
    const userPayload = { lastSeen: now };
    if (u.email)       userPayload.email = u.email;
    if (u.displayName) userPayload.displayName = u.displayName;

    // Top-level user doc — picked up by admin dashboard's onSnapshot.
    // merge:true preserves any existing fields (createdAt, custom rollups).
    await setDoc(doc(db, "users", uid), userPayload, { merge: true });
    // Mirror lastSeen to the profile doc so existing readers stay in sync.
    await setDoc(doc(db, "users", uid, "meta", "profile"), { lastSeen: now }, { merge: true });
  } catch (err) {
    // Heartbeat is best-effort — never bubble errors. Bad rules / offline /
    // quota exhaustion all just silently skip this tick.
    console.warn("[presence] ping failed:", err.message || err);
  }
}

/** Start (or restart) the presence heartbeat for the given uid. Idempotent. */
export function start(uid) {
  if (_state && _state.uid === uid && _state.intervalId) return;
  stop();
  _state = { uid, intervalId: null, paused: false };

  // First ping shortly after signin so the dashboard sees them immediately.
  setTimeout(() => { if (_state && _state.uid === uid) ping(uid); }, FIRST_PING_MS);

  _state.intervalId = setInterval(() => {
    if (!_state || _state.paused) return;
    ping(_state.uid);
  }, HEARTBEAT_MS);

  // Pause heartbeat when tab is hidden — no point burning writes when the
  // user isn't actively in the app. Resume + ping immediately on return.
  document.addEventListener("visibilitychange", _onVisibility);
}

/** Stop the heartbeat (call on signout). */
export function stop() {
  if (!_state) return;
  if (_state.intervalId) clearInterval(_state.intervalId);
  document.removeEventListener("visibilitychange", _onVisibility);
  _state = null;
}

function _onVisibility() {
  if (!_state) return;
  if (document.visibilityState === "hidden") {
    _state.paused = true;
  } else {
    _state.paused = false;
    // Coming back from hidden — ping right away so the user shows online.
    ping(_state.uid);
  }
}
